import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Col, Form, Row, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  useAdminMunicipalityAnalyticConfigs,
  useUpdateAdminMunicipalityAnalyticConfigs
} from 'api/hooks/use-admin-municipality-analytic-configs'
import { ActionButton } from 'shared/buttons'
import CenteredSpinner from 'shared/centered-spinner'
import TableFooter from 'shared/tables/table-footer'
import { configValueError, configValueUnchanged, formatConfigValue, parseConfigValue } from 'admin/analytics-data/format'

const errorMessage = (error) => error?.response?.data?.message || error?.message

const moduleNames = (configKeys) => [...new Set(configKeys.flatMap((configKey) => configKey.section_names))].sort()

const ConfigRow = ({ configKey, draft, onChange }) => {
  const stored = configKey.value
  const unset = stored === null || stored === undefined
  const key = configKey.config_key

  return (
    <tr data-testid="municipality-config-row">
      <td className="small">{configKey.section_names.join(', ')}</td>
      <td>
        <div className="fw-bold">{configKey.label}</div>
        <code className="small">{key}</code>
        {configKey.description && <div className="small text-muted">{configKey.description}</div>}
      </td>
      <td className="small">
        {configKey.analytics.map((analytic) => (
          <div key={analytic.id}>
            <Link to={`/admin/analytics-data/${analytic.id}`}>{analytic.key}</Link> {analytic.name}
          </div>
        ))}
      </td>
      <td className="small">{formatConfigValue(configKey.default_value)}</td>
      <td style={{ minWidth: '12rem' }}>
        <Form.Control
          size="sm"
          aria-label={key}
          value={key in draft ? draft[key] : formatConfigValue(stored)}
          placeholder={`Default: ${formatConfigValue(configKey.default_value)}`}
          className={unset ? 'border-warning' : ''}
          onChange={(e) => onChange(key, e.target.value)}
        />
      </td>
    </tr>
  )
}

// The thresholds this municipality's analytics run with, nightly and on
// demand. The same values the analytic pages edit, gathered per municipality.
const MunicipalityAnalyticConfigs = ({ municipalityId }) => {
  const { data, isLoading, error } = useAdminMunicipalityAnalyticConfigs({ municipalityId })
  const [draft, setDraft] = useState({})
  const [inputError, setInputError] = useState(null)
  const [moduleFilter, setModuleFilter] = useState('')
  const [pageParams, setPageParams] = useState({ page: 1, perPage: 25 })
  const updateMutation = useUpdateAdminMunicipalityAnalyticConfigs({
    municipalityId,
    options: {
      onSuccess: () => {
        setDraft({})
        toast.success('Analytic configuration saved.')
      }
    }
  })

  if (isLoading) return <CenteredSpinner />
  if (error) return <Alert variant="danger">Error loading analytic configuration: {errorMessage(error)}</Alert>

  const configKeys = data?.config_keys || []
  const byKey = Object.fromEntries(configKeys.map((configKey) => [configKey.config_key, configKey]))
  const edits = Object.entries(draft).filter(
    ([key, raw]) => !configValueUnchanged(raw, byKey[key].value, byKey[key].datatype)
  )
  const editCount = edits.length
  const filtered = moduleFilter
    ? configKeys.filter((configKey) => configKey.section_names.includes(moduleFilter))
    : configKeys
  const { page, perPage } = pageParams
  const pageKeys = filtered.slice((page - 1) * perPage, page * perPage)

  const handleModuleFilter = (value) => {
    setModuleFilter(value)
    setPageParams((prev) => ({ ...prev, page: 1 }))
  }

  const handleChange = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }))

  const handleSave = () => {
    const invalid = edits
      .map(([key, raw]) => {
        const message = configValueError(raw, byKey[key].datatype)
        return message && `${key}: ${message}`
      })
      .filter(Boolean)
    setInputError(invalid.join('; ') || null)
    if (invalid.length > 0) return

    const configs = {}
    edits.forEach(([key, raw]) => {
      const value = parseConfigValue(raw, byKey[key].datatype)
      if (value !== undefined) configs[key] = value
    })
    if (Object.keys(configs).length === 0) return setDraft({})
    updateMutation.mutate(configs)
  }

  return (
    <section id="analytic-configuration">
      {/* Zero height so it floats over the table; sticky only within this section, like Save Settings. */}
      <div className="sticky-top d-flex justify-content-end align-items-start" style={{ top: '20px', height: 0 }}>
        <ActionButton
          label={editCount > 0 ? `Save configuration (${editCount})` : 'Save configuration'}
          onClick={handleSave}
          size="md"
          className="shadow-sm"
          disabled={editCount === 0}
          loading={updateMutation.isLoading}
          loadingLabel="Saving"
        />
      </div>
      <h5 className="mb-3 mt-4 border-bottom pb-2">Analytic Configuration</h5>
      <p className="small text-muted">
        The thresholds this municipality’s analytics run with, both nightly and on demand. They are the same values
        set from an analytic’s page, so a key several analytics read changes for all of them. Arrays are
        comma-separated. Outlined cells have no value set and run with the key’s default. Edits are kept across
        pages and filters until you save.
      </p>
      {(inputError || updateMutation.error) && (
        <Alert variant="danger" className="small">
          {inputError || errorMessage(updateMutation.error)}
        </Alert>
      )}
      {configKeys.length === 0 ? (
        <Alert variant="info" className="mb-0">
          None of this municipality’s modules have analytics that read a config variable.
        </Alert>
      ) : (
        <>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Select
                size="sm"
                aria-label="Filter by module"
                value={moduleFilter}
                onChange={(e) => handleModuleFilter(e.target.value)}
              >
                <option value="">All modules</option>
                {moduleNames(configKeys).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <div className="table-responsive">
            <Table bordered size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Variable</th>
                  <th>Read by</th>
                  <th>Default</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {pageKeys.map((configKey) => (
                  <ConfigRow key={configKey.config_key} configKey={configKey} draft={draft} onChange={handleChange} />
                ))}
              </tbody>
            </Table>
          </div>
          <TableFooter
            params={pageParams}
            setParams={setPageParams}
            meta={{ total: filtered.length, pages: Math.max(1, Math.ceil(filtered.length / perPage)) }}
          />
        </>
      )}
    </section>
  )
}

export default MunicipalityAnalyticConfigs
